import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useDataProvider } from "react-admin";
import { useQuery } from "react-query";
import { Link as RouterLink } from "react-router-dom";
import { grants } from "../backoffice.access_control";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import type { DialogProps } from "../misc/DialogProps";
import { PdfViewer } from "../misc/PdfViewer";
import type { SignatureRequest, SignatureRequestType } from "../model/SignatureRequest";
import { studentDocumentsProvider } from "../providers/documentsProvider";
import type { Instructor } from "../providers/instructorsProvider";
import type { Student } from "../providers/studentsProvider";
import { gcs } from "../utils/storage";

const documentNames: Record<SignatureRequestType, string> = {
  ausbildungsnachweis: "Ausbildungsnachweis",
  schaltnachweis: "Schaltnachweis",
  asf_teilnahmebescheinigung: "ASF-Teilnahmebescheinigung",
  b196_teilnahmebescheinigung: "B196-Teilnahmebescheinigung",
  b96_teilnahmebescheinigung: "B96-Teilnahmebescheinigung",
};

export function SignatureFoldersDialog(
  props: {
    drivingSchoolId: string;
    onDocumentsLoaded: (count: number) => void;
  } & DialogProps,
) {
  const { drivingSchoolId, open, onClose, onDocumentsLoaded } = props;
  if (!grants.has("Document:viewSignatureFolder")) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="signature-folders-title">
      <DialogTitle
        id="signature-folders-title"
        sx={{ mr: 0, pr: 7, fontSize: { xs: 18, sm: 20 }, overflowWrap: "anywhere" }}
      >
        Unterschriften
        <wbr />
        mappe
      </DialogTitle>
      <DialogCloseButton onClick={onClose} />
      {open && (
        <_SignatureFoldersContent
          key={drivingSchoolId}
          drivingSchoolId={drivingSchoolId}
          onDocumentsLoaded={onDocumentsLoaded}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
}

function _SignatureFoldersContent(props: {
  drivingSchoolId: string;
  onDocumentsLoaded: (count: number) => void;
  onClose: () => void;
}) {
  const { drivingSchoolId, onDocumentsLoaded, onClose } = props;
  const dataProvider = useDataProvider();
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState<SignatureRequest>();
  const { data, isLoading, isFetching, isError, refetch } = useQuery(
    ["signatureFolders", drivingSchoolId],
    async () => {
      const documents = await studentDocumentsProvider.getSignatureFolderDocuments(drivingSchoolId);
      const studentIds = [...new Set(documents.map((document) => document.studentUid))];
      const [instructors, students] = await Promise.allSettled([
        dataProvider.getList<Instructor>("instructors", {
          filter: { drivingSchoolId },
          pagination: { page: 1, perPage: 10000 },
          sort: { field: "name", order: "ASC" },
        }),
        studentIds.length
          ? dataProvider.getMany<Student>("students", { ids: studentIds, meta: { drivingSchoolId } })
          : Promise.resolve({ data: [] as Array<Student> }),
      ]);
      return {
        documents,
        instructors: instructors.status === "fulfilled" ? instructors.value.data : [],
        students: students.status === "fulfilled" ? students.value.data : [],
        hasNameLookupError: instructors.status === "rejected" || students.status === "rejected",
      };
    },
    { onSuccess: (result) => onDocumentsLoaded(result.documents.length), refetchOnMount: "always" },
  );

  if (preview) {
    return (
      <_SignatureFolderPreview
        request={preview}
        drivingSchoolId={drivingSchoolId}
        onBack={() => setPreview(undefined)}
      />
    );
  }

  const studentsById = new Map(data?.students.map((student) => [student.id, student]));
  const instructorsById = new Map(data?.instructors.map((instructor) => [instructor.id, instructor]));
  const folders = new Map<string, { id: string; name: string; documents: Array<SignatureRequest> }>();
  for (const document of data?.documents ?? []) {
    const folderId = document.assignedDrivingSchoolManagerId || "unassigned";
    if (!folders.has(folderId)) {
      const instructor = instructorsById.get(folderId);
      folders.set(folderId, {
        id: folderId,
        name:
          folderId === "unassigned"
            ? "Nicht zugeordnet"
            : instructor
              ? `${instructor.name}${instructor.isActive === false ? " (inaktiv)" : ""}`
              : `Unbekannte Person (${folderId})`,
        documents: [],
      });
    }
    folders.get(folderId)!.documents.push(document);
  }
  const sortedFolders = [...folders.values()].sort((first, second) =>
    first.id === "unassigned" ? 1 : second.id === "unassigned" ? -1 : first.name.localeCompare(second.name, "de"),
  );
  const searchText = search.trim().toLocaleLowerCase("de");
  const visibleFolders = sortedFolders
    .map((folder) => ({
      ...folder,
      documents: folder.documents
        .filter((document) =>
          `${documentNames[document.type]} ${studentsById.get(document.studentUid)?.name ?? document.studentUid} ${document.drivingLicenseClass ?? ""}`
            .toLocaleLowerCase("de")
            .includes(searchText),
        )
        .sort(
          (first, second) =>
            (first.createdAt?.toMillis() ?? first.instructorSignedAt.toMillis()) -
            (second.createdAt?.toMillis() ?? second.instructorSignedAt.toMillis()),
        ),
    }))
    .filter((folder) => !searchText || folder.documents.length > 0);

  return (
    <DialogContent sx={{ minHeight: 300 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2, pt: 1 }}>
        <TextField
          label="Schüler oder Dokument suchen"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" aria-live="polite">
          {data
            ? `Ausstehende Unterschriften: ${data.documents.length} ${data.documents.length === 1 ? "Dokument" : "Dokumente"} in ${sortedFolders.length} ${sortedFolders.length === 1 ? "Mappe" : "Mappen"}`
            : "Dokumente werden geladen"}
        </Typography>
        <Tooltip title="Aktualisieren">
          <span>
            <IconButton disabled={isFetching} onClick={() => void refetch()} aria-label="Aktualisieren">
              {isFetching ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {data?.hasNameLookupError && !isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Einige Namen konnten nicht geladen werden. Bitte erneut aktualisieren.
        </Alert>
      )}
      {isError ? (
        <Alert severity="error">
          Die Unterschriftenmappen konnten nicht geladen werden. Bitte erneut aktualisieren.
        </Alert>
      ) : isLoading ? (
        <Box role="status" aria-label="Unterschriftenmappen werden geladen">
          <Skeleton height={48} />
          <Skeleton height={64} />
          <Skeleton height={64} />
        </Box>
      ) : !visibleFolders.length ? (
        <Typography sx={{ py: 4 }} color="text.secondary">
          {searchText ? "Keine passenden Dokumente gefunden." : "Keine offenen Dokumente vorhanden."}
        </Typography>
      ) : (
        <Table
          aria-label="Dokumente nach Unterschriftenmappe"
          size="small"
          sx={{
            tableLayout: "fixed",
            display: { xs: "block", sm: "table" },
            "& .MuiTableRow-root": { height: { xs: "auto" }, display: { xs: "block", sm: "table-row" } },
            "& .MuiTableCell-root": {
              overflowWrap: "anywhere",
              boxSizing: "border-box",
              display: { xs: "block", sm: "table-cell" },
            },
            "& .signature-document": {
              display: { xs: "grid", sm: "table-row" },
              gridTemplateColumns: "minmax(0, 1fr) 48px",
              py: { xs: 1, sm: 0 },
              borderBottomWidth: { xs: 1, sm: 0 },
              borderBottomStyle: "solid",
              borderBottomColor: "divider",
            },
            "& .signature-document > td": {
              display: { xs: "block", sm: "table-cell" },
              borderBottomWidth: { xs: 0, sm: 1 },
              borderBottomStyle: "solid",
              borderBottomColor: "divider",
            },
          }}
        >
          <Box component="colgroup" sx={{ display: { xs: "none", sm: "table-column-group" } }}>
            <col style={{ width: "31%" }} />
            <col style={{ width: "28%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "19%" }} />
            <col style={{ width: "10%" }} />
          </Box>
          {visibleFolders.map((folder) => (
            <TableBody
              key={folder.id}
              aria-label={folder.name}
              sx={{ display: { xs: "block", sm: "table-row-group" } }}
            >
              <TableRow sx={{ display: { xs: "block", sm: "table-row" } }}>
                <TableCell
                  colSpan={5}
                  component="th"
                  scope="rowgroup"
                  sx={{ bgcolor: "action.hover", py: 1.5, display: { xs: "block", sm: "table-cell" } }}
                >
                  <Typography component="h3" variant="subtitle2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                    {folder.name} · {folder.documents.length} {folder.documents.length === 1 ? "Dokument" : "Dokumente"}
                  </Typography>
                </TableCell>
              </TableRow>
              {!folder.documents.length && (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 2, color: "text.secondary" }}>
                    Keine Dokumente in dieser Mappe.
                  </TableCell>
                </TableRow>
              )}
              {folder.documents.map((document) => {
                const student = studentsById.get(document.studentUid);
                const name = student?.name ?? `Unbekannter Schüler (${document.studentUid})`;
                return (
                  <TableRow key={document.uid} className="signature-document">
                    <TableCell sx={{ gridColumn: 1 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <DescriptionOutlinedIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                        <Box component="span" sx={{ minWidth: 0 }}>
                          {documentNames[document.type]}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ gridColumn: 1 }}>
                      {student ? (
                        <Link component={RouterLink} to={`/students/${student.id}`} onClick={onClose}>
                          {name}
                        </Link>
                      ) : (
                        name
                      )}
                    </TableCell>
                    <TableCell sx={{ gridColumn: 1 }}>
                      <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                        Klasse:{" "}
                      </Box>
                      <Tooltip title="Führerscheinklasse" describeChild>
                        <span>{document.drivingLicenseClass ?? "–"}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ gridColumn: 1 }}>
                      <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                        Hinzugefügt:{" "}
                      </Box>
                      <Tooltip title="Hinzugefügt am" describeChild>
                        <span>{document.createdAt?.toFormat("dd.MM.yyyy") ?? "–"}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ gridColumn: 2, gridRow: "1 / span 4", alignSelf: "center", px: 0 }}>
                      <Tooltip title={document.previewStorageRef ? "Dokument ansehen" : "Keine Vorschau verfügbar"}>
                        <span>
                          <IconButton
                            aria-label={`${documentNames[document.type]} von ${name} ansehen`}
                            disabled={!document.previewStorageRef}
                            onClick={() => setPreview(document)}
                          >
                            <VisibilityOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          ))}
        </Table>
      )}
    </DialogContent>
  );
}

function _SignatureFolderPreview(props: { request: SignatureRequest; drivingSchoolId: string; onBack: () => void }) {
  const { request, drivingSchoolId, onBack } = props;
  const {
    data: pdf,
    isError,
    isFetching,
    refetch,
  } = useQuery(["signatureFolderPreview", drivingSchoolId, request.uid], async ({ signal }) => {
    const response = await fetch(await gcs.getDownloadUrl(request.previewStorageRef), { signal });
    if (!response.ok) throw new Error(`PDF download failed: ${response.status}`);
    return response.blob();
  });

  const downloadPdf = () => {
    if (!pdf) return;
    const url = URL.createObjectURL(pdf);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${documentNames[request.type]}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <DialogContent>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Tooltip title="Zurück zur Übersicht">
          <IconButton onClick={onBack} aria-label="Zurück zur Übersicht">
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Typography variant="subtitle1" sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
          {documentNames[request.type]}
        </Typography>
        <Tooltip title="Dokument herunterladen">
          <span>
            <IconButton onClick={downloadPdf} disabled={!pdf} aria-label="Dokument herunterladen">
              <DownloadIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      {isError ? (
        <Alert
          severity="error"
          action={
            <LoadingButton loading={isFetching} onClick={() => void refetch()}>
              Erneut laden
            </LoadingButton>
          }
        >
          Die Dokumentvorschau konnte nicht geladen werden.
        </Alert>
      ) : pdf ? (
        <PdfViewer pdf={pdf} sx={{ width: "100%", height: "60vh" }} />
      ) : (
        <Box role="status" aria-label="Dokumentvorschau wird geladen">
          <Skeleton variant="rectangular" height={300} />
        </Box>
      )}
    </DialogContent>
  );
}
